import { useEffect, useState, type ReactNode } from "react";
import { type SxProps, type Theme } from "@mui/material/styles";

import { useDisclosure } from "hooks/use-disclosure";
import { HiAcademicCap } from "react-icons/hi2";

import BackButton from "components/partials/back-button/back-button";
import { Box } from "ui/box";
import { mergeSx } from "ui/merge-sx";
import { Typography } from "ui/typography";
import { ConfirmationDialog } from "ui/confirmation-dialog";
import { StatusDisplay } from "components/partials/status-display/status-display";
import TouchpointIcon from "containers/admin/clients/touchpoint/components/touchpoint-icon/touchpoint-icon";
import { useBuilderAccess } from "containers/admin/clients/touchpoint/components/builder/use-builder-access";

import { useCurrentClient } from "hooks/api/use-clients";

import { displaySavedStatus } from "utilities";

import { Permission } from "types/auth";
import { BuilderContent } from "types";
import { TouchpointType } from "types/touchpoint";
import type { TouchpointVersionAttributes } from "hooks/api/use-touchpoint-versions";

interface NavbarWrapperProps {
  children?: ReactNode;
  sx?: SxProps<Theme>;
  "data-testid"?: string;
}
const NavbarWrapper = ({ sx, ...rest }: NavbarWrapperProps) => (
  <Box
    data-testid="navbar-wrapper"
    sx={mergeSx(
      {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        px: "32px",
        height: "44px",
        borderBottom: "1px solid",
        borderColor: "grey.200",
      },
      sx,
    )}
    {...rest}
  />
);

interface BuilderHeaderProps extends NavbarWrapperProps {
  apiValue: BuilderContent;
  contentName?: string;
  readonly?: boolean;
  permission: Permission;
  isSaved: boolean;
  setIsSaved: (isSaved: boolean) => void;
  isSaving: boolean;
}

export const BuilderHeader = ({
  apiValue,
  contentName,
  readonly = false,
  permission,
  isSaved,
  setIsSaved,
  isSaving,
  ...rest
}: BuilderHeaderProps) => {
  const { exitBuilder } = useBuilderAccess();

  const confirmExitModal = useDisclosure();

  const [shouldAlertBeforeExit, setShouldAlertBeforeExit] = useState(false);

  const [saveStatusMessage, setSaveStatusMessage] = useState<string>(
    displaySavedStatus(apiValue.lastModifiedDate),
  );

  useEffect(() => {
    const saveInProgress = "Saving changes...";
    let lastSaved = displaySavedStatus(apiValue.lastModifiedDate);

    if (isSaving) {
      setShouldAlertBeforeExit(true);
      setSaveStatusMessage(saveInProgress);
    }

    if (isSaved && !isSaving) {
      setShouldAlertBeforeExit(false);
      setSaveStatusMessage(lastSaved);
    }

    let pingInterval: ReturnType<typeof setInterval>;
    if (!readonly) {
      pingInterval = setInterval(() => {
        lastSaved = displaySavedStatus(apiValue.lastModifiedDate);
        setSaveStatusMessage(lastSaved);
      }, 5000);
    }

    // Cleanup function to clear interval
    return () => {
      if (pingInterval) {
        clearInterval(pingInterval);
      }
    };
  }, [isSaved, isSaving, apiValue.lastModifiedDate, readonly, saveStatusMessage]);

  function handleBackButton() {
    return shouldAlertBeforeExit ? confirmExitModal.onOpen() : goBack();
  }

  const goBack = () => {
    exitBuilder(apiValue as TouchpointVersionAttributes);
  };

  return (
    <NavbarWrapper
      data-testid="touchpoint-builder-header"
      sx={{ backgroundColor: "background.default" }}
      {...rest}>
      <Box sx={{ width: "20%" }}>
        <BackButton
          data-testid="builder-back-button"
          show
          size="medium"
          onClick={handleBackButton}
        />
      </Box>

      <TouchpointDetails width="60%" apiValue={apiValue} contentName={contentName} />

      <Box sx={{ width: "20%", textAlign: "right" }}>
        <Typography
          variant="body2"
          component="small"
          data-testid="autosave-message"
          sx={{ color: "text.secondary" }}>
          {saveStatusMessage}
        </Typography>
      </Box>

      {shouldAlertBeforeExit ? (
        <ConfirmationDialog
          open={confirmExitModal.isOpen}
          onClose={confirmExitModal.onClose}
          intent="error"
          headline={"Please wait. Saving changes..."}
          message={"Leaving now will result in the loss of unsaved data."}
          confirmButtonText="Exit Without Saving"
          onConfirm={goBack}
        />
      ) : (
        <ConfirmationDialog
          open={confirmExitModal.isOpen}
          onClose={confirmExitModal.onClose}
          intent="success"
          headline="Changes have been saved."
          message="Changes have been saved. You're safe to go back."
          confirmButtonText="Continue"
          onConfirm={goBack}
        />
      )}
    </NavbarWrapper>
  );
};

const TouchpointDetails = ({
  apiValue,
  contentName,
  width,
}: {
  apiValue: BuilderContent;
  contentName?: string;
  width: string;
}) => {
  const currentClient = useCurrentClient();

  const name = apiValue?.name || contentName || undefined;
  const version = apiValue?.version ? `V${apiValue.version}` : undefined;
  const status = apiValue?.status || "DRAFT";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        width,
        py: 1,
      }}
    >
      <Box sx={{ display: "flex", color: "grey.500" }}>
        <Box
          component={HiAcademicCap}
          data-testid="academic-icon"
          sx={{ width: "1em", height: "1em", flexShrink: 0, mt: "2px", mr: "0.25rem" }}
        />
        <Typography variant="inherit" component="span">
          {currentClient.name}
        </Typography>
      </Box>
      {name && (
        <Box sx={{ display: "flex", color: "secondary.main", alignItems: "center" }}>
          <TouchpointIcon
            touchpointType={apiValue.type ?? TouchpointType.LANDING_PAGE}
            className="mt-0.5 mr-1 size-4 shrink-0"
            data-testid="touchpoint-type-icon"
          />
          <Typography variant="h4" sx={{ color: "secondary.main" }}>
            {name}
          </Typography>
        </Box>
      )}
      {version && (
        <Typography variant="inherit" component="span">
          {version}
        </Typography>
      )}
      <StatusDisplay status={status} />
    </Box>
  );
};
